import { formatPropertyCode } from 'src/modules/shared/utils/catalog/property-code.util';
import { usePropertySelectorData } from '../../hooks/api/catalog/property-selector';
import { PropertyCardImage } from './property-card-image';
import { Text } from '../atoms/text';
import React, { useCallback, useMemo } from 'react';
import {
  type ComponentAppearance,
  type ComponentSize,
  type ComponentVariant,
} from 'src/modules/shared/types/component.type';
import SearchableSelect from 'src/modules/shared/components/atoms/inputs/searchable-select';
import type { ContainerPopoverPlacement } from 'src/modules/shared/types/popover.type';
import { PiArrowSquareOut } from 'react-icons/pi';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { useNavigate } from '@tanstack/react-router';
import { Route as CatalogPropertiesRoute } from 'src/routes/catalog/properties.index';
import { propertiesSearchSchema } from 'src/modules/catalog/schemas/properties-search.schema';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';

interface PropertySelectorProps {
  value?: number | null;
  onChange: (value: number | null) => void;
  disabled?: boolean;
  size?: ComponentSize;
  className?: string;
  placement?: ContainerPopoverPlacement;
  take?: number;
  showOpenButton?: boolean;
  onOpenProperty?: (propertyId: number) => void;
  menuMinWidth?: number;
  menuMaxWidth?: number;
  tooltipContent?: React.ReactNode;
  tooltipPlacement?: 'top' | 'bottom' | 'left' | 'right';
  variant?: ComponentVariant;
  appearance?: ComponentAppearance;
}

const CLEAR_VALUE = '__clear__';

export const PropertySelector: React.FC<PropertySelectorProps> = ({
  value,
  onChange,
  disabled = false,
  size = 'sm',
  className = '',
  placement = 'bottom-end',
  take = 20,
  showOpenButton = false,
  onOpenProperty,
  menuMinWidth = 240,
  menuMaxWidth = undefined,
  tooltipContent,
  tooltipPlacement = 'bottom',
  variant = 'primary',
  appearance = 'outline',
}) => {
  const navigate = useNavigate();
  const { t } = useI18n();

  const isPropertySelected = typeof value === 'number';
  const propertyId = isPropertySelected ? value : null;

  const {
    selectedProperty,
    searchedProperties,
    images,
    loading,
    onOpenChange,
    setSearch,
  } = usePropertySelectorData(propertyId, take);
  const selectedImage = images.get(String(propertyId));

  const options = useMemo(() => {
    const items = new Map<string, { value: string; content: string }>();

    const selectedId = selectedProperty?.id;
    if (selectedProperty && typeof selectedId === 'number') {
      const name = selectedProperty.name.trim();
      const code = formatPropertyCode({
        displayCode: selectedProperty.displayCode,
        fallback: `#${selectedId}`,
      });
      items.set(String(selectedId), {
        value: String(selectedId),
        content: name ? `${name} (${code})` : code,
      });
    } else if (typeof propertyId === 'number') {
      items.set(String(propertyId), {
        value: String(propertyId),
        content: `#${propertyId}`,
      });
    }

    searchedProperties.forEach((property) => {
      const name = property.name.trim();
      const code = formatPropertyCode({
        displayCode: property.displayCode,
        fallback: `#${property.id}`,
      });
      items.set(String(property.id), {
        value: String(property.id),
        content: name ? `${name} (${code})` : code,
      });
    });

    const mapped = Array.from(items.values()).map((opt) => ({
      value: opt.value,
      content: opt.content,
    }));

    return [
      { value: CLEAR_VALUE, content: t('shared.propertySelector.none') },
      { separator: true as const, key: 'separator-property-clear' },
      ...mapped,
    ];
  }, [propertyId, searchedProperties, selectedProperty, t]);

  const handlePropertyChange = useCallback(
    (val?: string) => {
      if (!val || val === CLEAR_VALUE) {
        if (propertyId == null) return;

        onChange(null);
        return;
      }

      const parsed = Number(val);
      if (!Number.isFinite(parsed)) return;
      if (parsed === propertyId) return;

      onChange(parsed);
    },
    [onChange, propertyId]
  );

  return (
    <div className={`flex items-center gap-1 min-w-0 max-w-full ${className}`}>
      <div className="flex-1 min-w-0">
        <Tooltip
          content={tooltipContent}
          placement={tooltipPlacement}
          size="sm"
        >
          <div className="w-full min-w-0">
            <SearchableSelect
              options={options}
              onOpenChange={onOpenChange}
              leadingIcon={
                isPropertySelected ? (
                  <span className="relative block h-6 w-6 overflow-hidden rounded">
                    <PropertyCardImage
                      imageUrl={selectedImage?.url ?? null}
                      alt=""
                      compact
                      loading={selectedImage?.loading ?? false}
                      emptyLabel={t(
                        'catalog.properties.list.placeholder.noImageTitle'
                      )}
                    />
                  </span>
                ) : undefined
              }
              renderOption={(option) => {
                if (option.value === CLEAR_VALUE) {
                  return <Text size="small">{option.content}</Text>;
                }
                const image = images.get(option.value);
                return (
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="relative h-10 w-10 shrink-0 overflow-hidden rounded-md">
                      <PropertyCardImage
                        imageUrl={image?.url ?? null}
                        alt=""
                        compact
                        loading={image?.loading ?? false}
                        emptyLabel={t(
                          'catalog.properties.list.placeholder.noImageTitle'
                        )}
                      />
                    </div>
                    <Text size="small" className="min-w-0 break-words">
                      {option.content}
                    </Text>
                  </div>
                );
              }}
              value={
                typeof propertyId === 'number'
                  ? String(propertyId)
                  : CLEAR_VALUE
              }
              onChange={handlePropertyChange}
              onQueryChange={setSearch}
              clientFilter={false}
              loading={loading}
              disabled={disabled}
              placeholder={t('shared.propertySelector.none')}
              size={size}
              variant={variant}
              appearance={appearance}
              placement={placement}
              menuMinWidth={menuMinWidth}
              menuMaxWidth={menuMaxWidth}
            />
          </div>
        </Tooltip>
      </div>

      {showOpenButton && (
        <Tooltip
          // Don't show tooltip for disabled button
          enabled={propertyId != null && !disabled}
          content={t('shared.propertySelector.open')}
          placement="bottom"
          size="sm"
        >
          <Button
            variant="neutral"
            appearance="ghost"
            size={size}
            isIconOnly
            disabled={disabled || propertyId == null}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (propertyId == null) return;

              if (onOpenProperty) {
                onOpenProperty(propertyId);
                return;
              }

              void navigate({
                to: CatalogPropertiesRoute.to,
                search: () =>
                  propertiesSearchSchema.parse({
                    search: String(propertyId),
                    activeStatus: 'ALL' as const,
                  }),
              });
            }}
          >
            <PiArrowSquareOut className="h-4 w-4" />
          </Button>
        </Tooltip>
      )}
    </div>
  );
};
